import { useLabLocations } from "./hooks";

import { BodyText, Button, Collapsible, HeaderText } from "@/components/ui";
import {
  type Address,
  capitalize,
  formatAddressIntoLines,
  formatPhoneNumber,
} from "@/lib/formatters";
import { formatDateTime } from "@/lib/time";
import { useS3SyncDownload } from "@/network/useS3SyncDownload";
import { DiagnosticReport, RequestGroup } from "@curai/js-fhir-tools";
import { FileDown } from "lucide-react";
import type {
  DxReportViewResponse,
  ReportOrderingClinician,
  ReportPatient,
} from "../../network/useDxReportView";

const mapValueByType = (value: string | undefined, type: string) => {
  switch (type) {
    case "date":
      return `${formatDateTime(value)}`;
    default:
      return value;
  }
};

const LabInfoSectionField = ({
  label,
  value,
  type = "string",
}: {
  label?: string;
  value: string | string[] | undefined;
  type?: "string" | "date";
}) => (
  <div className="flex flex-row items-start gap-1">
    {label && (
      <BodyText bold size="sm">
        {label}:
      </BodyText>
    )}
    {Array.isArray(value) ? (
      <div className="flex flex-col">
        {value.map((v) => (
          <BodyText size="sm" key={v} className="whitespace-pre-wrap">
            {mapValueByType(v, type)}
          </BodyText>
        ))}
      </div>
    ) : (
      <BodyText size="sm">{mapValueByType(value, type)}</BodyText>
    )}
  </div>
);

const LabInfoSection = ({
  title,
  children,
}: { title: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <HeaderText textColor="primary" size="md">
      {title}
    </HeaderText>
    <div>{children}</div>
  </div>
);

const ReportDetailsSection = ({
  dxReportView,
}: { dxReportView: DxReportViewResponse }) => {
  const {
    resultsStatus,
    labNetwork,
    accessionNumber,
    curaiOrderNumber,
    issuedAt,
    availablePdfs,
  } = dxReportView;
  const { mutateAsync: downloadDxReportPdf, isPending: isDownloadingPdf } =
    useS3SyncDownload();

  return (
    <LabInfoSection title="Report Details">
      <div className="flex flex-col gap-1">
        <LabInfoSectionField
          label="Report Status"
          value={DiagnosticReport.StatusLabels[resultsStatus]}
        />
        <LabInfoSectionField label="Issued" value={issuedAt} type="date" />
        <LabInfoSectionField
          label="Lab Network"
          value={
            RequestGroup.LAB_NETWORK_LABELS[labNetwork] ||
            labNetwork?.toUpperCase()
          }
        />
        {accessionNumber && (
          <LabInfoSectionField
            label="Accession Number"
            value={accessionNumber}
          />
        )}
        <LabInfoSectionField label="Order Number" value={curaiOrderNumber} />
        {availablePdfs.map((availablePdf) => {
          if (availablePdf.fileLocation) {
            return (
              <Button
                isLoading={isDownloadingPdf}
                hideTextOnLoading={false}
                key={`${availablePdf.fileName}`}
                className="w-fit"
                size="sm"
                variant="link"
                leftIcon={FileDown}
                onClick={() => {
                  downloadDxReportPdf({
                    path: availablePdf.fileLocation as string,
                  }).then((data) => {
                    const url = URL.createObjectURL(data);
                    window.open(url, "_blank");
                  });
                }}
              >
                Download Report
              </Button>
            );
          }
          return null;
        })}
      </div>
    </LabInfoSection>
  );
};

const PatientSection = ({ patient }: { patient: ReportPatient }) => {
  const {
    name,
    phone,
    address: reportAddress,
    dateOfBirth,
    adminGender,
  } = patient;

  const address = reportAddress
    ? ({
        line1: reportAddress.streetLines[0],
        line2: reportAddress.streetLines[1],
        city: reportAddress.city,
        state: reportAddress.state,
        zip: reportAddress.zipCode,
      } as Address)
    : undefined;

  return (
    <LabInfoSection title="Patient">
      <div className="flex flex-col gap-1">
        <LabInfoSectionField label="Name" value={name} />
        <LabInfoSectionField label="DOB" value={dateOfBirth} type="date" />
        {adminGender && (
          <LabInfoSectionField label="Gender" value={capitalize(adminGender)} />
        )}
        {phone && (
          <LabInfoSectionField label="Phone" value={formatPhoneNumber(phone)} />
        )}
        {address && (
          <LabInfoSectionField
            label="Address"
            value={formatAddressIntoLines(address)}
          />
        )}
      </div>
    </LabInfoSection>
  );
};

const ClinicianSection = ({
  clinician,
}: { clinician: ReportOrderingClinician }) => (
  <LabInfoSection title="Ordering Clinician">
    <div className="flex flex-col gap-1">
      <LabInfoSectionField label="Name" value={clinician.name} />
      {clinician.npi && (
        <LabInfoSectionField label="NPI" value={clinician.npi} />
      )}
      {clinician.phone && (
        <LabInfoSectionField
          label="Phone"
          value={formatPhoneNumber(clinician.phone)}
        />
      )}
    </div>
  </LabInfoSection>
);

const PerformingLabsSection = ({
  dxReportView,
}: { dxReportView: DxReportViewResponse }) => {
  const { allLabLocations } = useLabLocations(dxReportView.tests);

  return (
    <LabInfoSection title="Places of Service">
      <div className="flex flex-col gap-1">
        {allLabLocations.map((location, i) => {
          const address = location.address
            ? ({
                line1: location.address.streetLines[0],
                line2: location.address.streetLines[1],
                city: location.address.city,
                state: location.address.state,
                zip: location.address.zipCode,
              } as Address)
            : undefined;

          if (
            !address &&
            !location.facilityName &&
            !location.facilityContactName
          ) {
            return null;
          }

          return (
            <div key={`lab-location-${location.facilityName}`}>
              <BodyText
                bold
                size="sm"
                textColor="tertiary"
                className="flex flex-row items-start gap-1 mb-1"
              >
                Location {i + 1}
              </BodyText>
              {location.facilityName && (
                <LabInfoSectionField
                  label="Facility Name"
                  value={location.facilityName}
                />
              )}
              {location.facilityContactName && (
                <LabInfoSectionField
                  label="Facility Director"
                  value={location.facilityContactName}
                />
              )}
              {address && (
                <LabInfoSectionField
                  label="Address"
                  value={formatAddressIntoLines(address)}
                />
              )}
            </div>
          );
        })}
      </div>
    </LabInfoSection>
  );
};

export const ReportInfo = ({
  dxReportView,
}: { dxReportView: DxReportViewResponse }) => {
  const { patient, orderingClinician } = dxReportView;

  return (
    <Collapsible title="Info" open={true}>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
        <ReportDetailsSection dxReportView={dxReportView} />
        <PatientSection patient={patient} />
        {orderingClinician && (
          <ClinicianSection clinician={orderingClinician} />
        )}
        <div className="col-span-2">
          <PerformingLabsSection dxReportView={dxReportView} />
        </div>
      </div>
    </Collapsible>
  );
};
